import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  FileText,
  CurrencyEur,
  CreditCard,
  Clock,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Crown,
  MagnifyingGlass,
  CaretDown,
  CaretUp,
  Warning,
  Ticket,
  Plus,
  Trash,
  Copy,
  EnvelopeSimple
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ExternalApiPanel from '../components/ExternalApiPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthNames = {
  '01': 'Gen', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'Mag', '06': 'Giu',
  '07': 'Lug', '08': 'Ago', '09': 'Set', '10': 'Ott', '11': 'Nov', '12': 'Dic'
};

function SubscriptionBadge({ user }) {
  const now = new Date().toISOString();
  const isActive = user.subscription_ends_at && user.subscription_ends_at > now;
  const isCancelled = user.stripe_subscription_status === 'canceled' || user.stripe_subscription_status === 'cancelled';

  if (isCancelled) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5"><XCircle size={12} /> Disdetto</span>;
  }
  if (user.subscription_type === 'lifetime' && isActive) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5"><Crown size={12} /> A Vita</span>;
  }
  if (user.subscription_type === 'yearly' && isActive) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5"><Crown size={12} /> Annuale</span>;
  }
  if (user.subscription_type === 'monthly' && isActive) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5"><CreditCard size={12} /> Mensile</span>;
  }
  if (user.subscription_type && !isActive) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5"><XCircle size={12} /> Scaduto</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5"><Clock size={12} /> Trial</span>;
}

// ==================== Stripe Recovery Panel ====================
function StripeRecoveryPanel() {
  const [sessionId, setSessionId] = useState('');
  const [fallbackEmail, setFallbackEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleUnlock = async () => {
    if (!sessionId.trim()) { setError('Inserisci la session ID'); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/unlock-from-stripe-session`,
        { session_id: sessionId.trim(), email: fallbackEmail.trim() || undefined },
        { withCredentials: true }
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore durante lo sblocco');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="stripe-recovery-panel">
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-2">Sblocca utente da pagamento Stripe</h3>
        <p className="text-xs text-zinc-500 leading-relaxed mb-5">
          Usa questo pannello se un utente ha pagato su Stripe ma l'app non l'ha sbloccato (es. il verify è fallito, l'utente ha chiuso la tab dopo il pagamento).
          Copia la <strong>session ID</strong> dalla dashboard Stripe (inizia con <code className="bg-zinc-100 px-1">cs_live_</code> o <code className="bg-zinc-100 px-1">cs_test_</code>).
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">Stripe Session ID *</label>
            <Input
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="cs_live_a1..."
              className="rounded-none font-mono text-xs"
              data-testid="stripe-session-input"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">Email utente (fallback)</label>
            <Input
              value={fallbackEmail}
              onChange={(e) => setFallbackEmail(e.target.value)}
              placeholder="Serve solo se il metadata.user_id non corrisponde (raro)"
              className="rounded-none text-xs"
              data-testid="stripe-email-input"
            />
          </div>
          <Button
            onClick={handleUnlock}
            disabled={loading}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider text-xs"
            data-testid="stripe-unlock-btn"
          >
            {loading ? 'Sblocco in corso...' : '🔓 Sblocca abbonamento'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 p-4 text-xs text-red-800">
          <p className="font-bold uppercase tracking-wider mb-1">Errore</p>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 p-5">
          <p className="text-xs font-black uppercase tracking-wider text-emerald-800 mb-2">✅ Utente sbloccato</p>
          <div className="space-y-1 text-xs text-emerald-900">
            <p><strong>Email:</strong> {result.unlocked_user}</p>
            <p><strong>Subscription ID:</strong> <code className="bg-white px-1">{result.subscription_id}</code></p>
            <p><strong>Stato:</strong> {result.status}</p>
            <p><strong>Scadenza:</strong> {new Date(result.ends_at).toLocaleString('it-IT')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Payment Failed Recovery Panel ====================
function PaymentFailedRecoveryPanel() {
  const [email, setEmail] = useState('');
  const [bcc, setBcc] = useState('team@vitadahost.it');
  const [discountPercent, setDiscountPercent] = useState(10);
  const [applyDiscount, setApplyDiscount] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleRevoke = async () => {
    const target = email.trim().toLowerCase();
    if (!target) { setError("Inserisci l'email dell'utente"); return; }
    if (!window.confirm(
      `Stai per:\n` +
      `1) Revocare il premium a ${target}\n` +
      `2) Inviargli un'email "Pagamento non riuscito"\n` +
      (applyDiscount ? `3) Applicare uno sconto del ${discountPercent}% sul prossimo pagamento\n` : '') +
      `4) Mandare una copia a ${bcc || '(nessun BCC)'}\n\n` +
      `Confermi?`
    )) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/revoke-premium-and-notify`,
        {
          email: target,
          apply_discount: applyDiscount,
          discount_percent: discountPercent,
          bcc: bcc.trim() || '',
        },
        { withCredentials: true }
      );
      setResult(res.data);
      toast.success(`Revoca completata per ${target}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore durante la revoca';
      setError(detail);
      toast.error(detail);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="payment-failed-recovery-panel">
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-2">
          Revoca premium per pagamento fallito
        </h3>
        <p className="text-xs text-zinc-500 leading-relaxed mb-5">
          Usa questo pannello quando un utente risulta "abbonato" nel DB ma il pagamento Stripe è
          fallito (es. <code className="bg-zinc-100 px-1">incomplete</code>, <code className="bg-zinc-100 px-1">past_due</code>).
          In un click: revoca l'accesso premium, invia un'email empatica con istruzioni e applica
          opzionalmente uno sconto Stripe automatico per il prossimo rinnovo.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">
              Email utente *
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="utente@example.com"
              className="rounded-none text-xs"
              data-testid="payment-failed-email-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">
                Email BCC (copia per te)
              </label>
              <Input
                type="email"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="team@vitadahost.it"
                className="rounded-none text-xs"
                data-testid="payment-failed-bcc-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">
                Sconto % (next checkout)
              </label>
              <Input
                type="number"
                min={0}
                max={100}
                value={discountPercent}
                onChange={(e) => setDiscountPercent(parseInt(e.target.value) || 0)}
                className="rounded-none text-xs"
                disabled={!applyDiscount}
                data-testid="payment-failed-discount-input"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={applyDiscount}
              onChange={(e) => setApplyDiscount(e.target.checked)}
              className="w-4 h-4 accent-zinc-900"
              data-testid="payment-failed-apply-discount"
            />
            <span>Applica un coupon Stripe del <strong>{discountPercent}%</strong> sul prossimo pagamento</span>
          </label>

          <Button
            onClick={handleRevoke}
            disabled={loading || !email.trim()}
            className="rounded-none bg-red-700 hover:bg-red-800 text-white font-bold uppercase tracking-wider text-xs"
            data-testid="payment-failed-revoke-btn"
          >
            <EnvelopeSimple size={14} className="mr-2" weight="bold" />
            {loading ? 'In corso...' : 'Revoca + invia email'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 p-4 text-xs text-red-800" data-testid="payment-failed-error">
          <p className="font-bold uppercase tracking-wider mb-1">Errore</p>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 p-5" data-testid="payment-failed-result">
          <p className="text-xs font-black uppercase tracking-wider text-emerald-800 mb-3">
            ✅ Operazione completata
          </p>
          <div className="space-y-1.5 text-xs text-emerald-900">
            <p><strong>Email utente:</strong> {result.user_email}</p>
            <p>
              <strong>Premium revocato:</strong>{' '}
              {result.revoked ? `Sì (status → ${result.stripe_status_set})` : 'No'}
            </p>
            <p>
              <strong>Email "pagamento fallito" inviata:</strong>{' '}
              {result.email_sent ? `Sì (id ${result.email_id})` : `No — ${result.email_error || 'errore'}`}
            </p>
            <p>
              <strong>Sconto Stripe applicato:</strong>{' '}
              {result.discount_applied
                ? `Sì (${result.discount_percent}% sul prossimo pagamento)`
                : `No${result.discount_error ? ` — ${result.discount_error}` : ' (utente senza customer_id Stripe)'}`}
            </p>
            <p><strong>Copia inviata a:</strong> {result.bcc || '(nessuno)'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Unlock by Email Panel ====================
function UnlockByEmailPanel() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleUnlock = async () => {
    const target = email.trim().toLowerCase();
    if (!target) { setError("Inserisci l'email dell'utente"); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/unlock-by-email`,
        { email: target },
        { withCredentials: true }
      );
      setResult(res.data);
      toast.success(`${target} sbloccato!`);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore nello sblocco';
      setError(typeof detail === 'object' ? JSON.stringify(detail, null, 2) : detail);
      toast.error('Sblocco non riuscito — vedi dettagli sotto');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="unlock-email-panel">
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-2">
          Sblocca utente da email Stripe
        </h3>
        <p className="text-xs text-zinc-500 leading-relaxed mb-5">
          L'utente <strong>ha pagato</strong> su Stripe ma il sistema gli chiede ancora di pagare? 
          Questo accade quando il webhook Stripe non arriva a destinazione. Questo pannello cerca
          direttamente in Stripe (via email) e sincronizza l'utente nel DB.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">
              Email utente *
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="utente@example.com"
              className="rounded-none text-xs"
              data-testid="unlock-email-input"
            />
          </div>

          <Button
            onClick={handleUnlock}
            disabled={loading || !email.trim()}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-wider text-xs"
            data-testid="unlock-email-btn"
          >
            {loading ? 'Cerco in Stripe...' : '🔓 Sblocca da Stripe'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 p-4 text-xs text-red-800" data-testid="unlock-email-error">
          <p className="font-bold uppercase tracking-wider mb-1">Errore</p>
          <pre className="whitespace-pre-wrap font-mono text-[11px]">{error}</pre>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 p-5" data-testid="unlock-email-result">
          <p className="text-xs font-black uppercase tracking-wider text-emerald-800 mb-3">
            ✅ Utente sbloccato
          </p>
          <div className="space-y-1.5 text-xs text-emerald-900">
            <p><strong>Email:</strong> {result.unlocked_user}</p>
            <p><strong>Stripe Customer:</strong> <code className="bg-white px-1">{result.stripe_customer_id}</code></p>
            <p><strong>Subscription ID:</strong> <code className="bg-white px-1">{result.subscription_id}</code></p>
            <p><strong>Status:</strong> {result.status}</p>
            <p><strong>Tipo piano:</strong> {result.subscription_type}</p>
            <p><strong>Scadenza:</strong> {new Date(result.ends_at).toLocaleString('it-IT')}</p>
            <p><strong>Subscriptions Stripe esaminate:</strong> {result.candidates_inspected}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Email Diagnostics Panel ====================
function EmailDiagnosticsPanel() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [checkEmail, setCheckEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  const loadDiag = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-diagnostics`, { withCredentials: true });
      setDiag(res.data);
    } catch (e) {
      toast.error('Errore caricamento diagnostica');
    }
    setLoading(false);
  };

  useEffect(() => { loadDiag(); }, []);

  const handleTestSend = async () => {
    if (!testEmail.trim()) { toast.error('Inserisci un\'email'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/test-email`, { email: testEmail.trim() }, { withCredentials: true });
      setTestResult(res.data);
      if (res.data.ok) toast.success(`Email di test inviata a ${testEmail}`);
      else toast.error(`Invio fallito: ${res.data.error || 'errore sconosciuto'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
    setTesting(false);
  };

  const handleCheckBlocked = async () => {
    if (!checkEmail.trim()) { toast.error('Inserisci un\'email'); return; }
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await axios.get(`${API}/admin/email-blocked/${encodeURIComponent(checkEmail.trim())}`, { withCredentials: true });
      setCheckResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
    setChecking(false);
  };

  const handleUnblock = async (email) => {
    if (!window.confirm(`Rimuovere ${email} dalla blocklist Brevo?`)) return;
    try {
      const res = await axios.post(`${API}/admin/email-unblock/${encodeURIComponent(email)}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      handleCheckBlocked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const es = diag?.events_summary || {};
  const deliveryRate = es.requests > 0 ? ((es.delivered / es.requests) * 100).toFixed(1) : '—';

  return (
    <div className="space-y-6" data-testid="email-diag-panel">
      {loading && <p className="text-xs text-zinc-500">Caricamento...</p>}

      {/* Brevo stats */}
      {diag && (
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-4">📊 Stato Brevo (ultimi 30 giorni)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Inviate" value={es.requests || 0} />
            <Stat label="Consegnate" value={es.delivered || 0} color={es.delivered > 0 ? 'emerald' : 'zinc'} />
            <Stat label="Tasso consegna" value={`${deliveryRate}%`} color={parseFloat(deliveryRate) > 95 ? 'emerald' : 'amber'} />
            <Stat label="Crediti rimanenti" value={diag.account?.plans?.[0]?.credits?.toLocaleString('it-IT') || '?'} />
            <Stat label="Hard bounces" value={es.hard_bounces || 0} color={es.hard_bounces > 0 ? 'red' : 'zinc'} />
            <Stat label="Soft bounces" value={es.soft_bounces || 0} color={es.soft_bounces > 0 ? 'amber' : 'zinc'} />
            <Stat label="Bloccate" value={es.blocked || 0} color={es.blocked > 0 ? 'red' : 'zinc'} />
            <Stat label="Spam reports" value={es.spam_reports || 0} color={es.spam_reports > 0 ? 'red' : 'zinc'} />
          </div>
          <div className="text-[11px] text-zinc-500 space-y-1 border-t border-zinc-100 pt-3">
            <p><strong>Sender configurato:</strong> {diag.senders?.[0]?.email} ({diag.senders?.[0]?.active ? 'attivo' : 'INATTIVO ⚠️'})</p>
            <p><strong>DB utenti totali:</strong> {diag.db?.total_users} · <strong>Non verificati:</strong> {diag.db?.unverified_users}</p>
          </div>
          {(es.blocked > 5 || es.hard_bounces > 10) && (
            <div className="mt-4 bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              ⚠️ <strong>Attenzione:</strong> hai {es.blocked} email bloccate e {es.hard_bounces} hard bounces.
              Brevo non invia più automaticamente ad utenti già bouncati. Usa il pannello qui sotto per
              sbloccare singoli utenti.
            </div>
          )}
          <Button onClick={loadDiag} className="rounded-none mt-4 text-xs uppercase font-bold" variant="outline">
            🔄 Aggiorna
          </Button>
        </div>
      )}

      {/* Test send */}
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-2">✉️ Test invio</h3>
        <p className="text-xs text-zinc-500 mb-3">Manda un'email di test a un indirizzo per verificare la consegna.</p>
        <div className="flex gap-2">
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" className="rounded-none text-xs" data-testid="diag-test-input" />
          <Button onClick={handleTestSend} disabled={testing} className="rounded-none bg-zinc-900 text-white text-xs uppercase font-bold" data-testid="diag-test-btn">
            {testing ? '...' : 'Invia test'}
          </Button>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 text-xs ${testResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-red-50 border border-red-200 text-red-900'}`}>
            {testResult.ok ? `✅ Inviato. Brevo ID: ${testResult.id}` : `❌ ${testResult.error}`}
          </div>
        )}
      </div>

      {/* Check & unblock */}
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-2">🔍 Verifica blocklist</h3>
        <p className="text-xs text-zinc-500 mb-3">
          Controlla se un'email è nella blocklist di Brevo (causa #1 per "non mi arriva il codice").
          Se trovi un blocco, puoi rimuoverlo.
        </p>
        <div className="flex gap-2">
          <Input value={checkEmail} onChange={(e) => setCheckEmail(e.target.value)} placeholder="utente@example.com" className="rounded-none text-xs" data-testid="diag-check-input" />
          <Button onClick={handleCheckBlocked} disabled={checking} className="rounded-none text-xs uppercase font-bold" data-testid="diag-check-btn" variant="outline">
            {checking ? '...' : 'Verifica'}
          </Button>
        </div>
        {checkResult && (
          <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 text-xs space-y-1">
            <p><strong>Email:</strong> {checkResult.email}</p>
            <p><strong>Nella blocklist:</strong> {checkResult.email_blacklisted ? '🚨 SÌ' : '✅ No'}</p>
            {checkResult.contact_not_found && <p className="text-zinc-500">Contatto non in Brevo (mai inviato email a questa address)</p>}
            {checkResult.reason && <p className="text-red-700">{checkResult.reason}</p>}
            {checkResult.email_blacklisted && (
              <Button onClick={() => handleUnblock(checkResult.email)} className="mt-2 rounded-none bg-emerald-700 hover:bg-emerald-800 text-white text-xs uppercase font-bold" data-testid="diag-unblock-btn">
                🔓 Rimuovi dalla blocklist
              </Button>
            )}
          </div>
        )}
      </div>

      {diag?.errors?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 text-xs text-amber-900">
          <p className="font-bold mb-1">Errori diagnostica:</p>
          <ul className="list-disc pl-4 space-y-0.5">
            {diag.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color = 'zinc' }) {
  const colorMap = {
    zinc: 'text-zinc-900',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
  };
  return (
    <div className="border border-zinc-200 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
      <p className={`text-2xl font-black ${colorMap[color] || colorMap.zinc}`}>{value}</p>
    </div>
  );
}


export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromoLabel, setNewPromoLabel] = useState('');
  const [newPromoType, setNewPromoType] = useState('lifetime');
  const [newPromoMaxUses, setNewPromoMaxUses] = useState(1);
  const [newPromoUnlimited, setNewPromoUnlimited] = useState(false);
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [forcingLifetimeId, setForcingLifetimeId] = useState(null);
  const [qrTogglingId, setQrTogglingId] = useState(null);

  const handleGrantQrPlugin = async (userItem) => {
    const label = userItem.name ? `${userItem.name} (${userItem.email})` : userItem.email;
    if (!window.confirm(`Sbloccare a vita il Plugin QR Code per ${label}?\n\nEquivalente a un pagamento €2,99 andato a buon fine.`)) return;
    setQrTogglingId(userItem.id);
    try {
      const res = await axios.post(
        `${API}/admin/users/${userItem.id}/qrcode-plugin/grant`,
        {},
        { withCredentials: true },
      );
      if (res.data.already_active) {
        toast.info(`${label} ha già il Plugin QR Code attivo`);
      } else {
        toast.success(`✓ Plugin QR Code sbloccato a vita per ${label}`);
      }
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore sblocco QR Code');
    }
    setQrTogglingId(null);
  };

  const handleRevokeQrPlugin = async (userItem) => {
    const label = userItem.name ? `${userItem.name} (${userItem.email})` : userItem.email;
    if (!window.confirm(`Revocare il Plugin QR Code per ${label}?\n\nDa usare solo per rimborsi o errori.`)) return;
    setQrTogglingId(userItem.id);
    try {
      await axios.post(
        `${API}/admin/users/${userItem.id}/qrcode-plugin/revoke`,
        {},
        { withCredentials: true },
      );
      toast.success(`Plugin QR Code revocato per ${label}`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore revoca QR Code');
    }
    setQrTogglingId(null);
  };

  const handleForceLifetime = async (userItem) => {
    const label = userItem.name ? `${userItem.name} (${userItem.email})` : userItem.email;
    const msg =
      `Confermi l'attivazione LIFETIME per ${label}?\n\n` +
      `→ Cancellerà TUTTE le sue subscription Stripe attive (nessun addebito futuro)\n` +
      `→ Imposterà l'account come "a vita"\n\n` +
      `Azione IRREVERSIBILE lato Stripe.`;
    if (!window.confirm(msg)) return;
    setForcingLifetimeId(userItem.id);
    try {
      const res = await axios.post(
        `${API}/admin/force-lifetime`,
        { email: userItem.email, reason: `Attivazione manuale admin da dashboard` },
        { withCredentials: true },
      );
      const cancelled = res.data.stripe_subscriptions_cancelled || 0;
      const already = res.data.stripe_subscriptions_already_cancelled || 0;
      toast.success(
        `✓ ${label} passato a LIFETIME` +
          (cancelled > 0 ? ` · Cancellate ${cancelled} sub Stripe` : '') +
          (already > 0 ? ` (${already} già erano cancellate)` : '')
      );
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore attivazione lifetime');
    }
    setForcingLifetimeId(null);
  };

  const handleResendVerification = async (userId, email) => {
    if (!window.confirm(`Rinviare l'email di verifica a ${email}?`)) return;
    setResendingId(userId);
    try {
      const res = await axios.post(
        `${API}/admin/users/${userId}/resend-verification`,
        {},
        { withCredentials: true }
      );
      if (res.data.already_verified) {
        toast.info('Questa email è già verificata.');
      } else if (res.data.ok) {
        toast.success(`Email verificata rinviata a ${email}`);
      } else {
        toast.error(`Errore: ${res.data.error || 'invio fallito'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio');
    }
    setResendingId(null);
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchData();
    fetchPromoCodes();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [ovRes, usRes] = await Promise.all([
        axios.get(`${API}/admin/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true })
      ]);
      setOverview(ovRes.data);
      setUsers(usRes.data);
    } catch (err) {
      console.error('Admin fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/promo-codes`);
      setPromoCodes(res.data);
    } catch (err) {
      console.error('Promo codes fetch error:', err);
    }
  };

  const handleCreatePromo = async () => {
    setCreatingPromo(true);
    try {
      const res = await axios.post(`${API}/promo-codes`, {
        label: newPromoLabel,
        plan_type: newPromoType,
        max_uses: newPromoUnlimited ? -1 : newPromoMaxUses,
        is_unlimited: newPromoUnlimited,
      });
      toast.success(`Codice creato: ${res.data.code}`);
      setNewPromoLabel('');
      setNewPromoMaxUses(1);
      setNewPromoUnlimited(false);
      fetchPromoCodes();
    } catch (err) {
      toast.error('Errore nella creazione');
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleDeactivatePromo = async (code) => {
    if (!window.confirm(`Disattivare il codice ${code}?`)) return;
    try {
      await axios.delete(`${API}/promo-codes/${code}`);
      toast.success('Codice disattivato');
      fetchPromoCodes();
    } catch (err) {
      toast.error('Errore');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Codice copiato!');
  };

  const filteredUsers = users
    .filter(u => u.role !== 'admin')
    .filter(u =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.name.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';
      if (typeof valA === 'number') return sortDir === 'asc' ? valA - valB : valB - valA;
      return sortDir === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  const maxRegCount = overview?.registrations_by_month?.length
    ? Math.max(...overview.registrations_by_month.map(r => r.count))
    : 1;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-zinc-800 transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft size={22} weight="bold" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-black tracking-tight">Pannello Amministratore</h1>
              <p className="text-xs text-zinc-400 hidden sm:block">Gestione utenti e abbonamenti</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Crown size={16} weight="fill" className="text-amber-400" />
            <span className="hidden sm:inline">Admin</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Overview KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="admin-total-users">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} className="text-zinc-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Iscritti</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-zinc-900">{overview?.total_users || 0}</p>
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="admin-total-contracts">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={18} className="text-zinc-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Contratti</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-zinc-900">{overview?.total_contracts || 0}</p>
            <p className="text-xs text-green-600 mt-1">{overview?.signed_contracts || 0} firmati</p>
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="admin-subscriptions">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={18} className="text-zinc-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Abbonati</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-zinc-900">
              {(overview?.active_monthly || 0) + (overview?.active_yearly || 0)}
            </p>
            <div className="flex items-center gap-2 mt-1 text-xs">
              <span className="text-blue-600">{overview?.active_monthly || 0} mensili</span>
              <span className="text-zinc-300">|</span>
              <span className="text-amber-600">{overview?.active_yearly || 0} annuali</span>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="admin-cancelled">
            <div className="flex items-center gap-2 mb-2">
              <XCircle size={18} className="text-red-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Disdette</span>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-red-600">{overview?.cancelled || 0}</p>
            <p className="text-xs text-zinc-400 mt-1">{overview?.trial_users || 0} ancora in trial</p>
          </div>
        </div>

        {/* Registrations chart + Revenue */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="admin-registrations-chart">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4">Iscrizioni per mese</h3>
            {overview?.registrations_by_month?.length > 0 ? (
              <div className="space-y-2">
                {overview.registrations_by_month.map((item) => {
                  const [year, month] = item.month.split('-');
                  const label = `${monthNames[month] || month} ${year.slice(2)}`;
                  const width = Math.max((item.count / maxRegCount) * 100, 8);
                  return (
                    <div key={item.month} className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500 w-14 text-right font-medium">{label}</span>
                      <div className="flex-1 h-7 bg-zinc-100 relative">
                        <div className="h-full bg-zinc-900 transition-all duration-500" style={{ width: `${width}%` }} />
                      </div>
                      <span className="text-xs font-bold text-zinc-700 w-8">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 py-8 text-center">Nessun dato</p>
            )}
          </div>

          <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="admin-revenue-summary">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4">Riepilogo piattaforma</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">Fatturato totale contratti</span>
                <span className="text-lg font-black text-zinc-900">&euro;{overview?.platform_revenue?.toLocaleString('it-IT') || '0'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">Abbonamenti attivi mensili</span>
                <span className="text-sm font-bold text-blue-600">{overview?.active_monthly || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">Abbonamenti attivi annuali</span>
                <span className="text-sm font-bold text-amber-600">{overview?.active_yearly || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">Utenti in trial</span>
                <span className="text-sm font-bold text-zinc-700">{overview?.trial_users || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-zinc-600">Disdette totali</span>
                <span className="text-sm font-bold text-red-600">{overview?.cancelled || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b border-zinc-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'users' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-users"
          >
            <Users size={16} className="inline mr-2" />
            Utenti
          </button>
          <button
            onClick={() => setActiveTab('promo')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'promo' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-promo"
          >
            <Ticket size={16} className="inline mr-2" />
            Codici Promo
          </button>
          <button
            onClick={() => setActiveTab('stripe')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'stripe' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-stripe"
          >
            💳 Sblocca Stripe
          </button>
          <button
            onClick={() => setActiveTab('payment-failed')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'payment-failed' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-payment-failed"
          >
            🩹 Pagamento Fallito
          </button>
          <button
            onClick={() => setActiveTab('unlock-email')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'unlock-email' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-unlock-email"
          >
            🔓 Sblocca da email
          </button>
          <button
            onClick={() => setActiveTab('email-diag')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'email-diag' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-email-diag"
          >
            📧 Diagnostica Email
          </button>
          <button
            onClick={() => setActiveTab('external-api')}
            className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === 'external-api' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            data-testid="admin-tab-external-api"
          >
            🔗 API Vitadahost
          </button>
        </div>

        {activeTab === 'stripe' && <StripeRecoveryPanel />}
        {activeTab === 'payment-failed' && <PaymentFailedRecoveryPanel />}
        {activeTab === 'unlock-email' && <UnlockByEmailPanel />}
        {activeTab === 'email-diag' && <EmailDiagnosticsPanel />}
        {activeTab === 'external-api' && <ExternalApiPanel />}

        {activeTab === 'promo' && (
          <div className="space-y-6">
            {/* Create Promo */}
            <div className="bg-white border border-zinc-200 p-6" data-testid="admin-promo-create">
              <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-4">Genera nuovo codice</h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Etichetta</label>
                  <Input
                    value={newPromoLabel}
                    onChange={e => setNewPromoLabel(e.target.value)}
                    placeholder="Es. Concorso Instagram"
                    className="rounded-none mt-1"
                    data-testid="promo-label-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Tipo piano</label>
                  <select
                    value={newPromoType}
                    onChange={e => setNewPromoType(e.target.value)}
                    className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:border-zinc-900"
                    data-testid="promo-type-select"
                  >
                    <option value="lifetime">A Vita (gratuito per sempre)</option>
                    <option value="yearly">Annuale (1 anno gratis)</option>
                    <option value="quarterly">Trimestrale (3 mesi gratis)</option>
                    <option value="monthly">Mensile (1 mese gratis)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Utilizzi max</label>
                  <Input
                    type="number"
                    min={1}
                    value={newPromoUnlimited ? '' : newPromoMaxUses}
                    onChange={e => setNewPromoMaxUses(parseInt(e.target.value) || 1)}
                    disabled={newPromoUnlimited}
                    placeholder={newPromoUnlimited ? 'Illimitato' : ''}
                    className="rounded-none mt-1 disabled:bg-zinc-50 disabled:text-zinc-400"
                    data-testid="promo-max-uses-input"
                  />
                </div>
                <Button
                  onClick={handleCreatePromo}
                  disabled={creatingPromo}
                  className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm"
                  data-testid="promo-create-btn"
                >
                  <Plus size={16} className="mr-2" />
                  {creatingPromo ? 'Generazione...' : 'Genera codice'}
                </Button>
              </div>
              <label className="mt-4 flex items-center gap-2 cursor-pointer w-fit" data-testid="promo-unlimited-label">
                <input
                  type="checkbox"
                  checked={newPromoUnlimited}
                  onChange={e => setNewPromoUnlimited(e.target.checked)}
                  className="w-4 h-4 accent-zinc-900"
                  data-testid="promo-unlimited-checkbox"
                />
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                  Multi-uso illimitato · lo stesso codice funziona per un numero infinito di utenti diversi
                </span>
              </label>
            </div>

            {/* Promo List */}
            <div className="bg-white border border-zinc-200" data-testid="admin-promo-list">
              <div className="p-4 border-b border-zinc-200">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                  Codici generati ({promoCodes.length})
                </h3>
              </div>
              {promoCodes.length === 0 ? (
                <div className="p-8 text-center">
                  <Ticket size={32} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-sm text-zinc-400">Nessun codice promo creato</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {promoCodes.map((p) => (
                    <div key={p.code} className={`px-4 sm:px-6 py-4 flex items-center justify-between ${!p.active ? 'opacity-50' : ''}`} data-testid={`promo-row-${p.code}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono font-black text-sm tracking-wider text-zinc-900">{p.code}</span>
                          <button onClick={() => copyCode(p.code)} className="text-zinc-400 hover:text-zinc-700">
                            <Copy size={14} />
                          </button>
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 ${
                            p.plan_type === 'lifetime' ? 'bg-emerald-50 text-emerald-700' :
                            p.plan_type === 'yearly' ? 'bg-amber-50 text-amber-700' :
                            p.plan_type === 'quarterly' ? 'bg-purple-50 text-purple-700' :
                            'bg-blue-50 text-blue-700'
                          }`}>
                            {p.plan_type === 'lifetime' ? 'A Vita' :
                             p.plan_type === 'yearly' ? 'Annuale' :
                             p.plan_type === 'quarterly' ? 'Trimestrale' :
                             'Mensile'}
                          </span>
                          {(p.is_unlimited || p.max_uses === -1) && (
                            <span className="text-xs font-bold uppercase px-2 py-0.5 bg-zinc-900 text-white">∞ Multi-uso</span>
                          )}
                          {!p.active && <span className="text-xs text-red-500 font-bold">DISATTIVATO</span>}
                        </div>
                        <p className="text-xs text-zinc-500">
                          {p.label || 'Senza etichetta'} &middot; Usato {p.used_count}/{(p.is_unlimited || p.max_uses === -1) ? '∞' : p.max_uses}
                          {p.created_at && ` \u00b7 Creato ${p.created_at.split('T')[0]}`}
                        </p>
                      </div>
                      {p.active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivatePromo(p.code)}
                          className="rounded-none text-red-500 hover:text-red-700 flex-shrink-0 ml-4"
                          data-testid={`promo-deactivate-${p.code}`}
                        >
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
        <>
        {/* Users Table */}
        <div className="bg-white border border-zinc-200" data-testid="admin-users-table">
          <div className="p-4 sm:p-6 border-b border-zinc-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">
              Tutti gli iscritti ({filteredUsers.length})
            </h3>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per email o nome..."
                className="pl-9 pr-4 py-2 border border-zinc-300 text-sm w-full sm:w-72 focus:outline-none focus:border-zinc-900"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900" onClick={() => handleSort('name')}>
                    <span className="flex items-center gap-1">Nome <SortIcon field="name" /></span>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900" onClick={() => handleSort('email')}>
                    <span className="flex items-center gap-1">Email <SortIcon field="email" /></span>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Abbonamento</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900" onClick={() => handleSort('contracts_count')}>
                    <span className="flex items-center gap-1">Contratti <SortIcon field="contracts_count" /></span>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900" onClick={() => handleSort('revenue')}>
                    <span className="flex items-center gap-1">Fatturato <SortIcon field="revenue" /></span>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900" onClick={() => handleSort('created_at')}>
                    <span className="flex items-center gap-1">Iscritto <SortIcon field="created_at" /></span>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Scadenza</th>
                  <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-zinc-500">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-zinc-900">
                      <div className="flex items-center gap-2">
                        {u.name || '-'}
                        {!u.email_verified && (
                          <span
                            className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5"
                            title="Email non verificata"
                            data-testid={`badge-unverified-${u.id}`}
                          >
                            <Warning size={9} weight="bold" /> Non verif.
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-zinc-600">{u.email}</td>
                    <td className="py-3 px-4"><SubscriptionBadge user={u} /></td>
                    <td className="py-3 px-4 text-sm text-zinc-700">
                      {u.contracts_count} <span className="text-zinc-400">({u.signed_count} firmati)</span>
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-zinc-900">&euro;{u.revenue.toLocaleString('it-IT')}</td>
                    <td className="py-3 px-4 text-sm text-zinc-500">
                      {u.created_at ? format(new Date(u.created_at), 'dd/MM/yyyy') : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-zinc-500">
                      {u.subscription_ends_at ? format(new Date(u.subscription_ends_at), 'dd/MM/yyyy') : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {!u.email_verified && u.role !== 'admin' && (
                          <button
                            onClick={() => handleResendVerification(u.id, u.email)}
                            disabled={resendingId === u.id}
                            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-violet-700 hover:text-violet-900 disabled:opacity-50"
                            data-testid={`resend-verify-${u.id}`}
                          >
                            <EnvelopeSimple size={12} weight="bold" />
                            {resendingId === u.id ? '...' : 'Rinvia'}
                          </button>
                        )}
                        {u.role !== 'admin' && u.subscription_type !== 'lifetime' && (
                          <button
                            onClick={() => handleForceLifetime(u)}
                            disabled={forcingLifetimeId === u.id}
                            className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 px-2 py-1 transition-colors"
                            title="Attiva LIFETIME + cancella Stripe subscription"
                            data-testid={`force-lifetime-${u.id}`}
                          >
                            {forcingLifetimeId === u.id ? '...' : '🎁 Lifetime'}
                          </button>
                        )}
                        {u.role !== 'admin' && !u.has_qrcode_plugin && (
                          <button
                            onClick={() => handleGrantQrPlugin(u)}
                            disabled={qrTogglingId === u.id}
                            className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-2 py-1 transition-colors"
                            title="Sblocca a vita il Plugin QR Code (€2,99)"
                            data-testid={`grant-qrplugin-${u.id}`}
                          >
                            {qrTogglingId === u.id ? '...' : '🔓 QR'}
                          </button>
                        )}
                        {u.role !== 'admin' && u.has_qrcode_plugin && (
                          <button
                            onClick={() => handleRevokeQrPlugin(u)}
                            disabled={qrTogglingId === u.id}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50 px-2 py-0.5 transition-colors"
                            title="Revoca Plugin QR Code"
                            data-testid={`revoke-qrplugin-${u.id}`}
                          >
                            ✓ QR
                          </button>
                        )}
                        {u.subscription_type === 'lifetime' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                            ✓ Lifetime
                          </span>
                        )}
                        {u.email_verified && u.role !== 'admin' && u.subscription_type !== 'lifetime' && !forcingLifetimeId && !resendingId && (
                          null
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y divide-zinc-100">
            {filteredUsers.map((u) => (
              <div key={u.id} className="p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                >
                  <div>
                    <p className="text-sm font-bold text-zinc-900">{u.name || '-'}</p>
                    <p className="text-xs text-zinc-500">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <SubscriptionBadge user={u} />
                    <CaretDown size={14} className={`text-zinc-400 transition-transform ${expandedUser === u.id ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                {expandedUser === u.id && (
                  <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Contratti</span>
                      <span className="text-zinc-900">{u.contracts_count} ({u.signed_count} firmati)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Fatturato</span>
                      <span className="font-bold text-zinc-900">&euro;{u.revenue.toLocaleString('it-IT')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Alloggi</span>
                      <span className="text-zinc-700">{u.properties_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Iscritto il</span>
                      <span className="text-zinc-700">{u.created_at ? format(new Date(u.created_at), 'dd/MM/yyyy') : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Scadenza abb.</span>
                      <span className="text-zinc-700">{u.subscription_ends_at ? format(new Date(u.subscription_ends_at), 'dd/MM/yyyy') : '-'}</span>
                    </div>
                    {u.stripe_customer_id && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Stripe ID</span>
                        <span className="text-xs text-zinc-400 font-mono">{u.stripe_customer_id}</span>
                      </div>
                    )}
                    {!u.email_verified && u.role !== 'admin' && (
                      <div className="pt-2 border-t border-zinc-100">
                        <button
                          onClick={() => handleResendVerification(u.id, u.email)}
                          disabled={resendingId === u.id}
                          className="w-full inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-black uppercase tracking-wider py-2.5 disabled:opacity-50"
                          data-testid={`resend-verify-mobile-${u.id}`}
                        >
                          <EnvelopeSimple size={14} weight="bold" />
                          {resendingId === u.id ? 'Invio...' : 'Rinvia email verifica'}
                        </button>
                        <p className="text-[10px] text-amber-700 mt-1.5 text-center">⚠️ Email non verificata</p>
                      </div>
                    )}
                    {u.role !== 'admin' && u.subscription_type !== 'lifetime' && (
                      <div className="pt-2 border-t border-zinc-100">
                        <button
                          onClick={() => handleForceLifetime(u)}
                          disabled={forcingLifetimeId === u.id}
                          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider py-2.5 disabled:opacity-50"
                          data-testid={`force-lifetime-mobile-${u.id}`}
                        >
                          🎁 {forcingLifetimeId === u.id ? 'Attivo...' : 'Attiva LIFETIME + cancella Stripe'}
                        </button>
                        <p className="text-[10px] text-zinc-500 mt-1.5 text-center">Cancella subscription Stripe attive e passa al piano a vita</p>
                      </div>
                    )}
                    {u.role !== 'admin' && !u.has_qrcode_plugin && (
                      <div className="pt-2 border-t border-zinc-100">
                        <button
                          onClick={() => handleGrantQrPlugin(u)}
                          disabled={qrTogglingId === u.id}
                          className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-wider py-2.5 disabled:opacity-50"
                          data-testid={`grant-qrplugin-mobile-${u.id}`}
                        >
                          🔓 {qrTogglingId === u.id ? 'Sblocco...' : 'Sblocca Plugin QR Code a vita'}
                        </button>
                        <p className="text-[10px] text-zinc-500 mt-1.5 text-center">Equivalente a un pagamento €2,99 completato</p>
                      </div>
                    )}
                    {u.role !== 'admin' && u.has_qrcode_plugin && (
                      <div className="pt-2 border-t border-zinc-100 text-center space-y-2">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-1.5">
                          ✓ Plugin QR Code attivo
                        </span>
                        <button
                          onClick={() => handleRevokeQrPlugin(u)}
                          disabled={qrTogglingId === u.id}
                          className="block w-full text-[10px] uppercase tracking-wider text-red-600 hover:text-red-800 disabled:opacity-50"
                          data-testid={`revoke-qrplugin-mobile-${u.id}`}
                        >
                          {qrTogglingId === u.id ? 'Revoca...' : 'Revoca QR Code'}
                        </button>
                      </div>
                    )}
                    {u.subscription_type === 'lifetime' && (
                      <div className="pt-2 border-t border-zinc-100 text-center">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5">
                          ✓ Piano Lifetime attivo
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredUsers.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-400">
              {search ? 'Nessun utente trovato' : 'Nessun iscritto'}
            </div>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
